import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { FormattedMessage } from 'react-intl';
import { useCycleFromPreviousWeek } from '../../hooks/dialogs/useCycleFromPreviousWeek';
import { buildCycleLabel } from '../../hooks/dialogs/previousCycleWeeks';
import { useCreateTrainingCycleDialog } from '../../hooks/dialogs/useCreateTrainingCycleDialog';
import Stepper from '../shared/Stepper';
import WeekGridAssign, { WeekGridAssignment } from '../shared/WeekGridAssign';

interface ICreateTrainingCycleDialogProps {
  visible: boolean;
  onHide: () => void;
  clientId: number | string;
  setRefreshKey: (fn: (old: number) => number) => void;
}

type Flow = 'fromScratch' | 'fromTemplate' | 'fromPreviousCycle' | null;

const DURATION_CHIPS = [4, 8, 12, 16] as const;

/**
 * v2 cycle creation wizard (design handoff §4.2).
 *
 * Three TabPanels in the legacy UI replaced by a linear Stepper:
 *   Step 0 — paradigm picker (Desde cero / Desde plantilla)
 *   Step 1 — cycle data (when Desde cero) OR template picker (when Desde plantilla)
 *   Step 2 — assign workouts (only when Desde cero)
 *
 * The legacy hook keeps driving state — we don't touch its underlying
 * methods. The Global RPE toggle is hidden (per handoff §4.2: "REMOVIDO: el
 * RPE va por sesión, no por ciclo"); we still set `useGlobalRpe = false`
 * implicitly so per-assignment RPE selection shows when needed.
 *
 * Day-grid + drag&drop assignment UI (handoff §4.2 step 2) is a follow-up
 * polish — the existing dropdown-per-row UI keeps working and is shipped here.
 */
const CreateTrainingCycleDialog = ({ visible, onHide, clientId, setRefreshKey }: ICreateTrainingCycleDialogProps) => {
  const h = useCreateTrainingCycleDialog({ visible, onHide, clientId, setRefreshKey });
  // #23: tercer camino — reutilizar una semana de un ciclo anterior del alumno.
  const prev = useCycleFromPreviousWeek({
    visible,
    clientId,
    onDone: () => {
      setRefreshKey((old: number) => old + 1);
      onHide();
    }
  });

  const [flow, setFlow] = useState<Flow>(null);
  const [step, setStep] = useState(0);

  // Reset wizard state whenever the dialog is reopened.
  React.useEffect(() => {
    if (visible) {
      setFlow(null);
      setStep(0);
    }
  }, [visible]);

  const steps = useMemo(() => {
    if (flow === 'fromPreviousCycle') {
      return [
        { key: 'start', label: h.intl.formatMessage({ id: 'cycle.wizard.step.start' }) },
        { key: 'previous', label: h.intl.formatMessage({ id: 'cycle.wizard.fromPreviousCycle.title' }) }
      ];
    }
    if (flow === 'fromTemplate') {
      return [
        { key: 'start', label: h.intl.formatMessage({ id: 'cycle.wizard.step.start' }) },
        { key: 'template', label: h.intl.formatMessage({ id: 'cycle.wizard.fromTemplate.title' }) }
      ];
    }
    return [
      { key: 'start', label: h.intl.formatMessage({ id: 'cycle.wizard.step.start' }) },
      { key: 'data', label: h.intl.formatMessage({ id: 'cycle.wizard.step.data' }) },
      { key: 'assign', label: h.intl.formatMessage({ id: 'cycle.wizard.step.assign' }) }
    ];
  }, [flow, h.intl]);

  const goNext = () => {
    if (step === 1 && flow === 'fromScratch') {
      h.clickGoNextTab(); // syncs bodyCycle internal state
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goBack = () => {
    if (step === 0) return;
    setStep((s) => s - 1);
  };

  const onPickFlow = (next: Exclude<Flow, null>) => {
    setFlow(next);
    setStep(1);
  };

  const setDurationChip = (weeks: number) => {
    h.onDurationWeekChange(weeks);
  };

  // ── Step 0 — paradigm picker ──────────────────────────────────────────
  const renderParadigm = () => (
    <div className="et-cycle-wizard__paradigm">
      <button type="button" className="et-cycle-wizard__paradigm-card" onClick={() => onPickFlow('fromScratch')}>
        <i className="pi pi-sparkles" aria-hidden />
        <div className="et-cycle-wizard__paradigm-title">
          <FormattedMessage id="cycle.wizard.fromScratch.title" />
        </div>
        <div className="et-cycle-wizard__paradigm-desc">
          <FormattedMessage id="cycle.wizard.fromScratch.desc" />
        </div>
      </button>
      <button type="button" className="et-cycle-wizard__paradigm-card" onClick={() => onPickFlow('fromTemplate')}>
        <i className="pi pi-book" aria-hidden />
        <div className="et-cycle-wizard__paradigm-title">
          <FormattedMessage id="cycle.wizard.fromTemplate.title" />
        </div>
        <div className="et-cycle-wizard__paradigm-desc">
          <FormattedMessage id="cycle.wizard.fromTemplate.desc" />
        </div>
      </button>
      <button
        type="button"
        className="et-cycle-wizard__paradigm-card"
        onClick={() => onPickFlow('fromPreviousCycle')}
        disabled={prev.availableCycles.length === 0}
        title={
          prev.availableCycles.length === 0
            ? h.intl.formatMessage({
                id: 'cycle.wizard.fromPreviousCycle.empty',
                defaultMessage: 'Este alumno todavía no tiene ciclos con entrenamientos cargados'
              })
            : undefined
        }
      >
        <i className="pi pi-history" aria-hidden />
        <div className="et-cycle-wizard__paradigm-title">
          <FormattedMessage id="cycle.wizard.fromPreviousCycle.title" />
        </div>
        <div className="et-cycle-wizard__paradigm-desc">
          <FormattedMessage id="cycle.wizard.fromPreviousCycle.desc" />
        </div>
      </button>
    </div>
  );

  // ── Step 1 (fromScratch) — cycle data ─────────────────────────────────
  const renderCycleData = () => (
    <div className="et-cycle-wizard__step">
      <div className="p-field">
        <label htmlFor="cycleName">
          <FormattedMessage id="createCycle.cycleName" />
        </label>
        <InputText
          id="cycleName"
          value={h.cycleName}
          onChange={(e) => h.setCycleName(e.target.value)}
          className="w-full"
        />
      </div>

      <div className="p-field">
        <label>
          <FormattedMessage id="createCycle.durationInWeeks" />
        </label>
        <div className="et-cycle-wizard__chips">
          {DURATION_CHIPS.map((w) => {
            const active = h.durationInWeeks === w;
            return (
              <button
                key={w}
                type="button"
                onClick={() => setDurationChip(w)}
                className={`et-cycle-wizard__chip${active ? ' is-active' : ''}`}
              >
                {h.intl.formatMessage({ id: 'cycle.weekGrid.weekN' }, { n: w })}
              </button>
            );
          })}
          <InputNumber
            value={
              h.durationInWeeks && !DURATION_CHIPS.includes(h.durationInWeeks as 4 | 8 | 12 | 16)
                ? h.durationInWeeks
                : null
            }
            onValueChange={(e) => h.onDurationWeekChange(e.value ?? null)}
            mode="decimal"
            min={1}
            max={52}
            placeholder={h.intl.formatMessage({ id: 'common.custom', defaultMessage: 'Personalizada' })}
            className="et-cycle-wizard__chip-input"
            inputStyle={{ width: '90px', textAlign: 'center' }}
          />
        </div>
      </div>

      <div className="p-field">
        <label htmlFor="startDate">
          <FormattedMessage id="startDate" />
        </label>
        <Calendar
          id="startDate"
          value={h.startDate}
          locale={h.intl.locale}
          dateFormat="dd/mm/yy"
          onChange={(e) => h.setStartDate(e.value as Date | null)}
          showIcon
          className="w-full"
        />
      </div>

      <div className="p-field">
        <label htmlFor="clientFacingName">
          <FormattedMessage id="createCycle.clientFacingName" />
          <span className="text-xs ml-1" style={{ color: 'var(--ios-text-tertiary)' }}>
            (<FormattedMessage id="common.optional" />)
          </span>
        </label>
        <InputText
          id="clientFacingName"
          value={h.clientFacingName}
          onChange={(e) => h.setClientFacingName(e.target.value)}
          className="w-full"
        />
      </div>
    </div>
  );

  // ── Step 2 (fromScratch) — assign workouts (week grid drag&drop) ──────
  const gridAssignments: WeekGridAssignment[] = useMemo(
    () =>
      h.assignments
        .filter((a) => a.workoutId != null && a.dayOfWeek != null)
        .map((a) => ({ workoutId: a.workoutId as number, dayOfWeek: a.dayOfWeek as number })),
    [h.assignments]
  );

  const renderAssignWorkouts = () => (
    <div className="et-cycle-wizard__step">
      <WeekGridAssign
        workouts={h.workouts}
        durationInWeeks={h.durationInWeeks ?? 1}
        startDate={h.startDate}
        assignments={gridAssignments}
        onAssign={h.setAssignmentForDay}
        onUnassign={h.clearAssignmentForDay}
      />
    </div>
  );

  // ── Step 1 (fromTemplate) — template picker ───────────────────────────
  const renderTemplatePicker = () => (
    <div className="et-cycle-wizard__step">
      <div className="p-field">
        <label className="block mb-2">
          {h.intl.formatMessage(
            { id: 'clientDashboard.assignCycleTemplate.selectCycle' },
            { defaultMessage: 'Seleccionar Ciclo de Entrenamiento' }
          )}
        </label>
        <Dropdown
          value={h.selectedCycleTemplate}
          options={h.trainingCycleTemplates}
          onChange={(e) => h.handleCycleTemplateChange(e.value)}
          optionLabel="name"
          placeholder={h.intl.formatMessage(
            { id: 'clientDashboard.assignCycleTemplate.selectCyclePlaceholder' },
            { defaultMessage: 'Seleccione un ciclo de entrenamiento' }
          )}
          className="w-full"
        />
        {h.selectedCycleTemplate && (
          <p className="mt-2 text-sm">
            {h.selectedCycleTemplate.duration}{' '}
            {h.selectedCycleTemplate.isDurationInMonths
              ? h.intl.formatMessage({ id: 'common.months', defaultMessage: 'meses' })
              : h.intl.formatMessage({ id: 'common.weeks', defaultMessage: 'semanas' })}
          </p>
        )}
      </div>

      <div className="p-field">
        <label className="block mb-2">
          {h.intl.formatMessage({ id: 'common.startDate', defaultMessage: 'Fecha de Inicio' })}
        </label>
        <Calendar
          value={h.templateStartDate}
          onChange={(e) => h.handleTemplateStartDateChange(e.value as Date | null)}
          showIcon
          className="w-full"
          locale={h.intl.locale}
          dateFormat="dd/mm/yy"
        />
      </div>

      <div className="p-field">
        <label htmlFor="templateClientFacingName" className="block mb-2">
          <FormattedMessage id="createCycle.clientFacingName" />
          <span className="text-xs ml-1" style={{ color: 'var(--ios-text-tertiary)' }}>
            (<FormattedMessage id="common.optional" />)
          </span>
        </label>
        <InputText
          id="templateClientFacingName"
          value={h.templateClientFacingName}
          onChange={(e) => h.setTemplateClientFacingName(e.target.value)}
          className="w-full"
        />
      </div>
    </div>
  );

  // ── Footer ────────────────────────────────────────────────────────────
  const isLastStep = step === steps.length - 1;
  const canContinue =
    flow === 'fromPreviousCycle'
      ? prev.canSubmit
      : flow === 'fromScratch'
        ? step === 1
          ? !!h.cycleName && !!h.startDate && !!h.durationInWeeks
          : true
        : step === 1
          ? !!h.selectedCycleTemplate && !!h.templateStartDate
          : true;

  const onFinish = () => {
    if (flow === 'fromPreviousCycle') {
      prev.submit();
    } else if (flow === 'fromScratch') {
      h.handleAction();
    } else {
      h.handleAssignCycleTemplate();
    }
  };

  // ── Step 1 (fromPreviousCycle) — ciclo + semana de origen (#23) ────────
  const renderPreviousCyclePicker = () => (
    <div className="et-cycle-wizard__step">
      <div className="p-field">
        <label className="block mb-2">
          <FormattedMessage id="cycle.wizard.fromPreviousCycle.pickCycle" />
        </label>
        <Dropdown
          value={prev.sourceCycleId}
          options={prev.availableCycles.map((c) => ({ label: buildCycleLabel(c, h.intl.locale), value: c.id }))}
          onChange={(e) => prev.selectCycle(e.value)}
          placeholder={h.intl.formatMessage({ id: 'cycle.wizard.fromPreviousCycle.pickCyclePlaceholder' })}
          className="w-full"
          loading={prev.loading}
        />
      </div>

      {prev.weekOptions.length > 0 && (
        <div className="p-field">
          <label className="block mb-2">
            <FormattedMessage id="cycle.wizard.fromPreviousCycle.pickWeek" />
          </label>
          {/* Se muestran TODAS las semanas, con la última preseleccionada: es
              donde quedó la carga más alta, pero el coach puede volver a una
              anterior si la última fue de descarga. */}
          <div className="et-cycle-wizard__chips">
            {prev.weekOptions.map((w) => (
              <button
                key={w.index}
                type="button"
                onClick={() => prev.setSourceWeekIndex(w.index)}
                className={`et-cycle-wizard__chip${prev.sourceWeekIndex === w.index ? ' is-active' : ''}`}
                disabled={w.sessionsWithWork === 0}
                title={
                  w.sessionsWithWork === 0
                    ? h.intl.formatMessage({ id: 'cycle.wizard.fromPreviousCycle.emptyWeek' })
                    : undefined
                }
              >
                {h.intl.formatMessage({ id: 'cycle.weekGrid.weekN' }, { n: w.weekNumber })}
                {w.isRecommended ? ' ★' : ''}
              </button>
            ))}
          </div>
          {prev.sourceWeekIndex !== null && (
            <p className="mt-2 text-sm" style={{ color: 'var(--ios-text-secondary)' }}>
              {h.intl.formatMessage(
                { id: 'cycle.wizard.fromPreviousCycle.weekSummary' },
                { n: prev.weekOptions[prev.sourceWeekIndex]?.sessionsWithWork ?? 0 }
              )}
            </p>
          )}
        </div>
      )}

      <div className="p-field">
        <label className="block mb-2">
          <FormattedMessage id="createCycle.cycleName" />
        </label>
        <InputText value={prev.name} onChange={(e) => prev.setName(e.target.value)} className="w-full" />
      </div>

      <div className="p-field">
        <label className="block mb-2">
          <FormattedMessage id="createCycle.durationInWeeks" />
        </label>
        <div className="et-cycle-wizard__chips">
          {DURATION_CHIPS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => prev.setDurationInWeeks(n)}
              className={`et-cycle-wizard__chip${prev.durationInWeeks === n ? ' is-active' : ''}`}
            >
              {h.intl.formatMessage({ id: 'cycle.weekGrid.weekN' }, { n })}
            </button>
          ))}
        </div>
      </div>

      <div className="p-field">
        <label className="block mb-2">
          {h.intl.formatMessage({ id: 'common.startDate', defaultMessage: 'Fecha de Inicio' })}
        </label>
        <Calendar
          value={prev.startDate}
          onChange={(e) => prev.setStartDate(e.value as Date | null)}
          showIcon
          className="w-full"
          locale={h.intl.locale}
          dateFormat="dd/mm/yy"
        />
      </div>
    </div>
  );

  const renderFooter = () => {
    if (step === 0) return null;
    return (
      <div className="et-cycle-wizard__footer">
        <Button
          label={h.intl.formatMessage({ id: 'common.back', defaultMessage: 'Atrás' })}
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={goBack}
        />
        {isLastStep ? (
          <Button
            label={h.intl.formatMessage({ id: 'createCycle.button.create', defaultMessage: 'Crear ciclo' })}
            icon="pi pi-check"
            onClick={onFinish}
            loading={h.loading || prev.saving}
            disabled={!canContinue}
            style={{ background: 'var(--et-cta-bg)', borderColor: 'var(--et-cta-bg)', color: 'var(--et-cta-fg)' }}
          />
        ) : (
          <Button
            label={h.intl.formatMessage({ id: 'common.continue', defaultMessage: 'Continuar' })}
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={goNext}
            disabled={!canContinue}
          />
        )}
      </div>
    );
  };

  // The "Asignar workouts" step needs a much wider canvas to fit the 7-day grid
  // comfortably. Other steps stay at the original width.
  const isAssignStep = flow === 'fromScratch' && step === 2;
  const dialogWidth = isAssignStep ? 'min(1140px, 96vw)' : 'min(720px, 95vw)';

  return (
    <Dialog
      draggable={false}
      resizable={false}
      dismissableMask
      header={h.intl.formatMessage({ id: 'createCycle.dialog.header' })}
      className="responsive-dialog"
      visible={visible}
      style={{ width: dialogWidth }}
      onHide={onHide}
      footer={renderFooter()}
    >
      <Stepper steps={steps} current={step} onStepClick={(i) => setStep(i)} />

      {step === 0 && renderParadigm()}
      {step === 1 && flow === 'fromScratch' && renderCycleData()}
      {step === 2 && flow === 'fromScratch' && renderAssignWorkouts()}
      {step === 1 && flow === 'fromTemplate' && renderTemplatePicker()}
      {step === 1 && flow === 'fromPreviousCycle' && renderPreviousCyclePicker()}
    </Dialog>
  );
};

export default CreateTrainingCycleDialog;
